import { Injectable } from '@angular/core';
import {
	ActivatedRoute,
	ActivatedRouteSnapshot,
	CanActivate,
	Router,
	RouterStateSnapshot,
	UrlTree,
} from '@angular/router';
import { DataHttpService, IGetUserSettingsResult } from '@valhalla/data/http';
import { Observable, from } from 'rxjs';
import { SessionUser } from '@valhalla/core';
import { map, switchMap, shareReplay, filter } from 'rxjs/operators';
import { trimProps, lowerCaseMapper } from '@valhalla/utils';

@Injectable({ providedIn: 'root' })
export class CanActivateDefaultPage implements CanActivate {
	constructor(
		readonly router: Router,
		readonly route: ActivatedRoute,
		readonly httpData: DataHttpService,
		readonly sessionUser: SessionUser
	) {}

	userSettings$ = this.sessionUser.userInfo$.pipe(
		filter(ui => !!ui),
		map(ui => ui.id),
		switchMap(userId => this.httpData.users.getSettings(userId)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	firstLoad = true;

	startPage$ = this.userSettings$;

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		const redirectByQueryParams$ = this.redirectByQueryParams(route);

		if (redirectByQueryParams$) {
			return redirectByQueryParams$;
		}

		return this.startPage$.pipe(
			switchMap(userSettings => {
				if (
					this.firstLoad &&
					(!userSettings.userUISettings.useStartUpPage ||
						(!userSettings.userUISettings.userStartPageType && userSettings.userUISettings.useStartUpPage) ||
						(userSettings.userUISettings.useStartUpPage &&
							userSettings.userUISettings.userStartPageType === 'Default' &&
							!userSettings.startPage))
				) {
					this.firstLoad = false;
					return this.router.navigate(['/feeds/default']);
				} else if (this.firstLoad && userSettings.userUISettings.useStartUpPage) {
					this.firstLoad = false;
					return this.openStartPage(userSettings);
				}

				return this.openStartPage(userSettings);
			})
		);
	}

	openStartPage(userSettings: IGetUserSettingsResult) {
		if (!userSettings.userUISettings.userStartPageType) {
			return this.router.navigate(['/feeds/default']);
		}

		if (userSettings.userUISettings.userStartPageType === 'Lenta') {
			return this.router.navigate(['feeds/default']);
		}

		if (userSettings.userUISettings.userStartPageType === 'Agenda') {
			return this.router.navigate(['/link'], {
				queryParams: {
					url: `/NewCustomGrid.aspx?force=grid&Type=FromYou&UserID=${this.sessionUser.userId}`,
				},
			});
		}

		if (userSettings.userUISettings.userStartPageType === 'Default') {
			const defaultPageLink: string = userSettings.startPage;

			const spaNoFrameLinkPart = 'spa/noframe';
			if (defaultPageLink.includes(spaNoFrameLinkPart)) {
				const spaLink = defaultPageLink.substr(defaultPageLink.indexOf(spaNoFrameLinkPart) + spaNoFrameLinkPart.length);
				return this.router.navigate([spaLink]);
			}

			const spaLinkPart = 'spa/';
			if (defaultPageLink.includes(spaLinkPart)) {
				const spaLink = defaultPageLink.substr(defaultPageLink.indexOf(spaLinkPart) + spaLinkPart.length - 1);
				return this.router.navigate([spaLink]);
			}

			const portalLink = '/portal/';
			if (defaultPageLink.includes(portalLink)) {
				const strPid = defaultPageLink.substr(defaultPageLink.indexOf(portalLink) + portalLink.length);
				const portalId = Number(strPid);
				if (!isNaN(portalId)) {
					return this.router.navigate([`/portal/${portalId}`]);
				}
			}

			const aspxLink = '.aspx';
			if (defaultPageLink.includes(aspxLink)) {
				return this.router.navigate(['/link'], {
					queryParams: {
						url: defaultPageLink,
					},
				});
			}

			return this.router.navigateByUrl(defaultPageLink);
		}
	}

	redirectByQueryParams(route: ActivatedRouteSnapshot): Observable<boolean> {
		const hasQueryParams = Object.keys(route.queryParams).length > 0;

		if (!hasQueryParams) {
			return;
		}

		const lowerCaseParams = trimProps({ ...route.queryParams }, lowerCaseMapper);
		const getParam = (par: string) => lowerCaseParams[par.toLowerCase()];

		const taskId = getParam('taskId');
		if (taskId) {
			return from(this.router.navigate(['/tasks', taskId]));
		}
	}
}
